import { useState, useEffect, useCallback } from "react";
import { useTranslation as useI18nTranslation } from "react-i18next";
import { useAuthContext } from "../contexts/AuthContext";
import { languageApi } from "../services/api/language.api";
import { translationService } from "../services/translation.service";
import { SupportedLanguage } from "../i18n/config";
import {
  formatDate,
  formatNumber,
  formatRelativeTime,
} from "../utils/i18n/format";

interface UseUnifiedTranslationOptions {
  namespace?: string;
  enableDynamicTranslation?: boolean;
  enableCaching?: boolean;
}

export const useUnifiedTranslation = (
  options: UseUnifiedTranslationOptions = {},
) => {
  const {
    namespace,
    enableDynamicTranslation = true,
    enableCaching = true,
  } = options;

  type SupportedNs =
    | "common"
    | "events"
    | "dashboard"
    | "widgets"
    | "tasks"
    | "notifications"
    | "support"
    | "documents"
    | "test";
  const { t, i18n } = useI18nTranslation(namespace as SupportedNs | undefined);
  const { user, isAuthenticated } = useAuthContext();
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [translationCache, setTranslationCache] = useState<Map<string, string>>(
    new Map(),
  );

  // Базовые функции локализации
  const translate = useCallback(
    (key: string, options?: Record<string, any>) => {
      return options !== undefined ? t(key ?? "", options) : t(key ?? "");
    },
    [t],
  );

  const translatePlural = useCallback(
    (key: string, count: number, options?: Record<string, any>) => {
      return t(key, { count, ...options });
    },
    [t],
  );

  // Форматирование дат и чисел
  const formatDateTime = useCallback(
    (date: Date | string, format: "short" | "long" = "short") => {
      return formatDate(date, format);
    },
    [],
  );

  const formatCurrency = useCallback((amount: number) => {
    return formatNumber(amount, "currency");
  }, []);

  const formatDecimal = useCallback((number: number) => {
    return formatNumber(number, "decimal");
  }, []);

  const formatTimeAgo = useCallback((date: Date | string) => {
    return formatRelativeTime(date);
  }, []);

  // Динамический перевод
  const translateDynamic = useCallback(
    async (text: string, targetLanguage?: string): Promise<string> => {
      if (!enableDynamicTranslation || !text.trim()) {
        return text;
      }

      const currentLanguage =
        targetLanguage || (i18n.language as SupportedLanguage);

      // Если текст уже на целевом языке, возвращаем как есть
      if (currentLanguage === "ru" || currentLanguage === i18n.language) {
        return text;
      }

      // Проверяем кэш
      if (enableCaching) {
        const cacheKey = `${text}:${currentLanguage}`;
        if (translationCache.has(cacheKey)) {
          return translationCache.get(cacheKey)!;
        }
      }

      setIsTranslating(true);
      setTranslationError(null);

      try {
        // Сначала пробуем серверный API
        const response = await languageApi.translateContent(
          text,
          currentLanguage,
          "ru",
        );

        if (response.success && response.data) {
          const translatedText = response.data.translatedText;

          // Сохраняем в кэш
          if (enableCaching) {
            const cacheKey = `${text}:${currentLanguage}`;
            setTranslationCache((prev) =>
              new Map(prev).set(cacheKey, translatedText),
            );
          }

          return translatedText;
        } else {
          throw new Error("Server translation failed");
        }
      } catch (serverError) {
        console.warn(
          "Server translation failed, trying Google Translate:",
          serverError,
        );

        try {
          // Fallback на Google Translate
          const translatedText = await translationService.translateText(
            text,
            currentLanguage,
            "ru",
          );

          // Сохраняем в кэш
          if (enableCaching) {
            const cacheKey = `${text}:${currentLanguage}`;
            setTranslationCache((prev) =>
              new Map(prev).set(cacheKey, translatedText),
            );
          }

          return translatedText;
        } catch (googleError) {
          console.error("All translation methods failed:", googleError);
          setTranslationError("Translation failed");
          return text; // Возвращаем оригинальный текст при ошибке
        }
      } finally {
        setIsTranslating(false);
      }
    },
    [i18n.language, enableDynamicTranslation, enableCaching, translationCache],
  );

  // Пакетный перевод
  const translateBatch = useCallback(
    async (texts: string[], targetLanguage?: string): Promise<string[]> => {
      if (!enableDynamicTranslation || texts.length === 0) {
        return texts;
      }

      const currentLanguage =
        targetLanguage || (i18n.language as SupportedLanguage);

      if (currentLanguage === "ru" || currentLanguage === i18n.language) {
        return texts;
      }

      setIsTranslating(true);
      setTranslationError(null);

      try {
        // Сначала пробуем серверный API
        const response = await languageApi.translateBatch(
          texts,
          currentLanguage,
          "ru",
        );

        if (response.success && response.data) {
          const translatedTexts = response.data.translatedTexts;

          // Сохраняем в кэш
          if (enableCaching) {
            texts.forEach((text, index) => {
              const val = translatedTexts[index];
              if (val != null) {
                const cacheKey = `${text}:${currentLanguage}`;
                setTranslationCache((prev) => new Map(prev).set(cacheKey, val));
              }
            });
          }

          return translatedTexts;
        } else {
          throw new Error("Server batch translation failed");
        }
      } catch (serverError) {
        console.warn(
          "Server batch translation failed, trying Google Translate:",
          serverError,
        );

        try {
          const translatedTexts = await translationService.translateBatch(
            texts,
            currentLanguage,
            "ru",
          );

          if (enableCaching) {
            texts.forEach((text, index) => {
              const val = translatedTexts[index];
              if (val != null) {
                const cacheKey = `${text}:${currentLanguage}`;
                setTranslationCache((prev) => new Map(prev).set(cacheKey, val));
              }
            });
          }

          return translatedTexts;
        } catch (googleError) {
          console.error("All batch translation methods failed:", googleError);
          setTranslationError("Batch translation failed");
          return texts;
        }
      } finally {
        setIsTranslating(false);
      }
    },
    [i18n.language, enableDynamicTranslation, enableCaching],
  );

  // Смена языка
  const changeLanguage = useCallback(
    async (language: SupportedLanguage) => {
      try {
        // Сначала переключаем язык в i18next
        await i18n.changeLanguage(language);

        // Сохраняем в localStorage как fallback
        localStorage.setItem("selectedLanguage", language);

        // Сохраняем языковые настройки на сервере, если пользователь авторизован
        if (isAuthenticated && user) {
          try {
            await languageApi.updateLanguagePreference({
              language,
              region: getRegionFromLanguage(language),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            if (process.env["NODE_ENV"] === "development") {
              // eslint-disable-next-line no-console
              console.log("✅ Language preference saved to server");
            }
          } catch (apiError) {
            console.warn(
              "⚠️ Failed to save language preference to server, using localStorage fallback:",
              apiError,
            );
          }
        }

        // Очищаем кэш переводов при смене языка
        if (enableCaching) {
          setTranslationCache(new Map());
        }
      } catch (error) {
        console.error("Failed to change language:", error);
        throw error;
      }
    },
    [i18n, isAuthenticated, user, enableCaching],
  );

  // Вспомогательная функция для определения региона по языку
  const getRegionFromLanguage = (language: SupportedLanguage): string => {
    const regionMap: Record<SupportedLanguage, string> = {
      ru: "RU",
      en: "GB",
      de: "DE",
      fr: "FR",
      es: "ES",
    };
    return regionMap[language] || "US";
  };

  // Очистка кэша
  const clearCache = useCallback(() => {
    setTranslationCache(new Map());
  }, []);

  // Очищаем кэш при смене языка
  useEffect(() => {
    if (enableCaching) {
      setTranslationCache(new Map());
    }
  }, [i18n.language, enableCaching]);

  return {
    // Базовые функции i18next
    t: translate,
    tPlural: translatePlural,
    i18n,
    currentLanguage: i18n.language as SupportedLanguage,

    // Форматирование
    formatDateTime,
    formatCurrency,
    formatDecimal,
    formatTimeAgo,

    // Динамический перевод
    translateDynamic,
    translateBatch,
    isTranslating,
    translationError,

    // Управление языком
    changeLanguage,
    getCurrentLanguage: () => i18n.language as SupportedLanguage,
    isRTL: i18n.dir() === "rtl",

    // Кэширование
    clearCache,
    cacheSize: translationCache.size,
  };
};

export default useUnifiedTranslation;
